/**
 * Frontend-only UI counter for How Often preview: replays ordered observation
 * snapshots per maneuver+observation(+role) key using predefined vs non-predefined rules.
 */

export interface ObAttemptSnapshot {
	activeAttempt: number;
	totalAttempt: number;
}

export interface PredefinedUiCounterState {
	numerator: number;
	denominator: number;
	pendingUnobserved: number;
	preset: number;
}

export interface NonPredefinedUiCounterState {
	numerator: number;
	denominator: number;
}

export function applyPredefinedObserved(s: PredefinedUiCounterState): PredefinedUiCounterState {
	return {
		...s,
		numerator: s.numerator + 1,
		denominator: s.denominator + 1,
	};
}

export function applyPredefinedUnobserved(s: PredefinedUiCounterState): PredefinedUiCounterState {
	let numerator = s.numerator - 1;
	let pendingUnobserved = s.pendingUnobserved + 1;
	let denominator = s.denominator;
	if (pendingUnobserved > s.preset) {
		numerator += 1;
		denominator += 1;
		pendingUnobserved -= 1;
	}
	return { ...s, numerator, denominator, pendingUnobserved };
}

export function applyNonPredefinedObserved(s: NonPredefinedUiCounterState): NonPredefinedUiCounterState {
	return { numerator: s.numerator + 1, denominator: s.denominator + 1 };
}

export function applyNonPredefinedUnobserved(s: NonPredefinedUiCounterState): NonPredefinedUiCounterState {
	return { numerator: s.numerator, denominator: s.denominator + 1 };
}

function clampNonNegative(n: number): number {
	return n < 0 ? 0 : n;
}

/**
 * Replays transitions from `prev` toward `next` using at most one dimension
 * change per step (matches cumulative addRemoveOb patterns).
 */
export function transitionPredefinedCounter(
	state: PredefinedUiCounterState,
	prev: ObAttemptSnapshot,
	next: ObAttemptSnapshot,
): PredefinedUiCounterState {
	let a0 = prev.activeAttempt;
	let t0 = prev.totalAttempt;
	const a = next.activeAttempt;
	const t = next.totalAttempt;
	let s = state;
	let guard = 0;
	while ((a0 !== a || t0 !== t) && guard++ < 5000) {
		if (a0 < a && t0 < t) {
			s = applyPredefinedObserved(s);
			a0 += 1;
			t0 += 1;
		} else if (a0 > a && t0 < t) {
			s = applyPredefinedUnobserved(s);
			a0 -= 1;
			t0 += 1;
		} else if (a0 < a) {
			s = applyPredefinedObserved(s);
			a0 += 1;
		} else if (a0 > a) {
			s = applyPredefinedUnobserved(s);
			a0 -= 1;
		} else if (t0 < t) {
			s = applyPredefinedUnobserved(s);
			t0 += 1;
		} else if (t0 > t) {
			t0 -= 1;
			s = { ...s, denominator: Math.max(0, s.denominator - 1) };
		} else {
			break;
		}
	}
	return s;
}

export function transitionNonPredefinedCounter(
	state: NonPredefinedUiCounterState,
	prev: ObAttemptSnapshot,
	next: ObAttemptSnapshot,
): NonPredefinedUiCounterState {
	let a0 = prev.activeAttempt;
	let t0 = prev.totalAttempt;
	const a = next.activeAttempt;
	const t = next.totalAttempt;
	let s = state;
	let guard = 0;
	while ((a0 !== a || t0 !== t) && guard++ < 5000) {
		if (a0 < a && t0 < t) {
			s = applyNonPredefinedObserved(s);
			a0 += 1;
			t0 += 1;
		} else if (a0 > a && t0 < t) {
			s = applyNonPredefinedUnobserved(s);
			a0 -= 1;
			t0 += 1;
		} else if (a0 < a) {
			s = applyNonPredefinedObserved(s);
			a0 += 1;
		} else if (a0 > a) {
			s = applyNonPredefinedUnobserved(s);
			a0 -= 1;
		} else if (t0 < t) {
			s = applyNonPredefinedUnobserved(s);
			t0 += 1;
		} else if (t0 > t) {
			t0 -= 1;
			s = { ...s, denominator: Math.max(0, s.denominator - 1) };
		} else {
			break;
		}
	}
	return s;
}

export function replayUiCounterForKey(params: {
	isPredefinedKey: boolean;
	presetFromForm: number;
	chronology: ObAttemptSnapshot[];
}): { numerator: number; denominator: number } {
	const { isPredefinedKey, presetFromForm, chronology } = params;

	if (isPredefinedKey) {
		const preset = Math.max(0, presetFromForm);
		let state: PredefinedUiCounterState = {
			numerator: preset,
			denominator: preset,
			pendingUnobserved: 0,
			preset,
		};
		let prev: ObAttemptSnapshot = { activeAttempt: preset, totalAttempt: preset };
		for (const snap of chronology) {
			if (snap.activeAttempt === prev.activeAttempt && snap.totalAttempt === prev.totalAttempt) continue;
			state = transitionPredefinedCounter(state, prev, snap);
			prev = { activeAttempt: snap.activeAttempt, totalAttempt: snap.totalAttempt };
		}
		return {
			numerator: clampNonNegative(state.numerator),
			denominator: Math.max(0, state.denominator),
		};
	}

	let state: NonPredefinedUiCounterState = { numerator: 0, denominator: 0 };
	let prev: ObAttemptSnapshot = { activeAttempt: 0, totalAttempt: 0 };
	for (const snap of chronology) {
		if (snap.activeAttempt === prev.activeAttempt && snap.totalAttempt === prev.totalAttempt) continue;
		state = transitionNonPredefinedCounter(state, prev, snap);
		prev = { activeAttempt: snap.activeAttempt, totalAttempt: snap.totalAttempt };
	}
	return {
		numerator: clampNonNegative(state.numerator),
		denominator: Math.max(0, state.denominator),
	};
}

export function completionFromUiCounter(numerator: number, denominator: number): number {
	if (denominator <= 0) return 0;
	return +((numerator / denominator) * 100).toFixed(2);
}

export function maneuverObservationKey(maneuverCode: string, observationCode: string): string {
	return `${maneuverCode}::${observationCode}`;
}

/**
 * Matches offline `howOftenObservationKey`.
 * `matchBySeat` is true for TS1 v3 so PF and PM stay apart.
 * Old TS1 passes false so a PF/PM removal replaces a blank-role preset.
 */
export function howOftenUiObservationKey(
	obs: {
		maneuverCode: string;
		observationCode: string;
		candidateRole?: string | null;
	},
	matchBySeat = true,
): string {
	if (matchBySeat) {
		const role = (obs.candidateRole ?? "").trim();
		if (role) {
			return `${obs.maneuverCode}::${obs.observationCode}::${role}`;
		}
	}
	return maneuverObservationKey(obs.maneuverCode, obs.observationCode);
}

function observationCodeFromHowOftenUiKey(key: string): string {
	const parts = key.split("::");
	// maneuver::observation  OR  maneuver::observation::role
	return parts[1] ?? "";
}

function dedupeConsecutiveSnapshots(list: ObAttemptSnapshot[]): ObAttemptSnapshot[] {
	const out: ObAttemptSnapshot[] = [];
	for (const s of list) {
		const last = out[out.length - 1];
		if (last && last.activeAttempt === s.activeAttempt && last.totalAttempt === s.totalAttempt) continue;
		out.push(s);
	}
	return out;
}

/**
 * Sums UI counter numerators/denominators per category for How Often preview.
 * Branch split mirrors `rateHowOftenCategories` (TS1 category with zero admin
 * predefined obs uses the no-predefined path).
 */
export function aggregateHowOftenUiCountersPreview(params: {
	filteredPredefinedObs: {
		maneuverCode: string;
		observationCode: string;
		totalAttempt: number;
		activeAttempt: number;
		candidateRole?: string | null;
	}[];
	orderedNewObs: {
		maneuverCode: string;
		observationCode: string;
		totalAttempt: number;
		activeAttempt: number;
		candidateRole?: string | null;
	}[];
	formTypeCode?: string;
	/** TS1 v3 keeps PF/PM apart. Old TS1 matches maneuver + OB only. */
	matchBySeat?: boolean;
}): {
	predefinedBranch: Map<string, { activeAttempts: number; totalAttempts: number }>;
	noPredefinedBranch: Map<string, { activeAttempts: number; totalAttempts: number }>;
} {
	const { filteredPredefinedObs, orderedNewObs, formTypeCode, matchBySeat = true } = params;
	const isTs1 = formTypeCode === "ts_1";

	const predefinedSet = new Set<string>();
	const presetByKey = new Map<string, number>();
	const categoriesWithPredefined = new Set<string>();
	for (const obs of filteredPredefinedObs) {
		if (obs.totalAttempt <= 0) continue;
		const key = howOftenUiObservationKey(obs, matchBySeat);
		predefinedSet.add(key);
		presetByKey.set(key, obs.totalAttempt);
		categoriesWithPredefined.add(obs.observationCode.split("_")[0].toUpperCase());
	}

	const chronologyByKey = new Map<string, ObAttemptSnapshot[]>();
	for (const obs of orderedNewObs) {
		const key = howOftenUiObservationKey(obs, matchBySeat);
		const list = chronologyByKey.get(key) ?? [];
		list.push({ activeAttempt: obs.activeAttempt, totalAttempt: obs.totalAttempt });
		chronologyByKey.set(key, list);
	}

	const allKeys = new Set<string>([...predefinedSet, ...chronologyByKey.keys()]);

	const predefinedBranch = new Map<string, { activeAttempts: number; totalAttempts: number }>();
	const noPredefinedBranch = new Map<string, { activeAttempts: number; totalAttempts: number }>();

	for (const key of allKeys) {
		const observationCode = observationCodeFromHowOftenUiKey(key);
		const category = observationCode.split("_")[0].toUpperCase();
		const isNoPredefinedPath = Boolean(isTs1 && !categoriesWithPredefined.has(category));
		const isPredefinedKey = predefinedSet.has(key);
		const preset = presetByKey.get(key) ?? 0;
		const chronology = dedupeConsecutiveSnapshots(chronologyByKey.get(key) ?? []);

		const { numerator, denominator } = replayUiCounterForKey({
			isPredefinedKey,
			presetFromForm: preset,
			chronology,
		});

		const branchMap = isNoPredefinedPath ? noPredefinedBranch : predefinedBranch;
		const cur = branchMap.get(category) ?? { activeAttempts: 0, totalAttempts: 0 };
		cur.activeAttempts += numerator;
		cur.totalAttempts += denominator;
		branchMap.set(category, cur);
	}

	return { predefinedBranch, noPredefinedBranch };
}
